import * as React from "react";
import { Link } from "react-router-dom"
import ChangePasswordInput from "../../core/ChangePasswordInput/ChangePasswordInput";
import Spinner from "../../core/Spinner/Spinner";
import debounce from "../../helpers/debounce";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IChangePasswordResponse from "../../types/IChangePasswordResponse";
import IPasswordIsValidResponse from "../../types/IPasswordIsValidResponse";
import IPasswordRequirement from "../../types/IPasswordRequirement";
import IResponse from "../../types/IResponse";

enum ProcessStateValues {
    changePassword,
    changePasswordComplete,
    error
}

interface IChangePasswordProps extends ITranslationProps {
    changePassword: (oldPassword: string, password: string) => Promise<IResponse<IChangePasswordResponse> | null>;
    validatePasswordRequirements(password: string): Promise<IResponse<IPasswordIsValidResponse> | null>;
}

interface IChangePasswordState {
    // state control
    processState: ProcessStateValues,
    processing?: boolean;
    validated: boolean,

    // response from validating the key
    passwordRequirements: IPasswordRequirement[],

    // values entered by the user
    oldPassword: string,
    password: string,
    confirmPassword: string;

    // error messages
    isOldPasswordInvalid: boolean,
    isNewPasswordInvalid: boolean,
    isConfirmPasswordInvalid: boolean;
    areFieldInvalid: boolean;
    isChangePasswordFailure: boolean,
}

class ChangePassword extends React.Component<IChangePasswordProps, IChangePasswordState> {
    private formElement: HTMLFormElement | null;

    // We are making an API call to verify the password is correct.  But we don't want to make
    // this call on every key press.  So we are using a debounce function to only make the call
    // after the user has stopped typing for a certain period of time.
    // https://www.carlrippon.com/search-after-stop-typing-react-component/
    private validatePasswordWhenUserStoppedTyping = debounce(
        async () => {
            // call the web service to see if the password meets the requirements
            // update the display to show the user which requirements are not met
            const passwordRequirements = await this.props.validatePasswordRequirements(this.state.password);
            if (passwordRequirements && passwordRequirements.success) {
                this.setState({ passwordRequirements: passwordRequirements.payload.requirements });
            }
        },
        500);

    constructor(props: IChangePasswordProps) {
        super(props);

        this.state = {
            processState: ProcessStateValues.changePassword,
            processing: false,
            validated: false,
            passwordRequirements: [],
            oldPassword: "",
            password: "",
            confirmPassword: "",
            isOldPasswordInvalid: false,
            isNewPasswordInvalid: false,
            isConfirmPasswordInvalid: false,
            areFieldInvalid: false,
            isChangePasswordFailure: false,
        };

        // From the ChangePasswordInput control
        this.onChangePasswordInputSubmit = this.onChangePasswordInputSubmit.bind(this);
        this.onPasswordChanged = this.onPasswordChanged.bind(this);

        // Local events
        this.onOldPasswordChange = this.onOldPasswordChange.bind(this);
        this.inputKeypress = this.inputKeypress.bind(this);
        this.onSubmitPasswordChange = this.onSubmitPasswordChange.bind(this);
    }

    public render() {
        return (
            <div>
                {this.state.processing && <Spinner />}
                <h2>{this.props.t("ChangePassword.Title")}</h2>
                {this.getContent()}
            </div>
        );
    }

    public async componentDidMount() {
        this.setState({ processing: true });
        const response = await this.props.validatePasswordRequirements("");

        if (response && response.success) {
            this.setState({
                passwordRequirements: response.payload.requirements,
            });
        }
        this.setState({ processing: false });
    }

    private getContent() {
        switch (this.state.processState) {
            case ProcessStateValues.changePassword:
                return (
                    <div>
                        <div className="mb-3 mt-3">{this.props.t("ChangePassword.Instructions")}</div>
                        <form ref={ref => this.formElement = ref} className={this.state.validated ? "was-validated" : "needs-validation"} noValidate={true}>
                            <div className="form-group">
                                <label>{this.props.t("ChangePassword.OldPassword")}</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder={this.props.t("ChangePassword.OldPassword")}
                                    value={this.state.oldPassword}
                                    onChange={this.onOldPasswordChange}
                                    onKeyPress={this.inputKeypress}
                                    required={true}
                                    autoFocus={true} />

                                <div className="invalid-feedback">
                                    {this.props.t("ChangePassword.PasswordValidationMessage")}
                                </div>
                                {this.state.isOldPasswordInvalid &&
                                    <div className="alert alert-warning">
                                        {this.props.t("ChangePassword.OldPasswordIsInvalid")}
                                    </div>
                                }
                            </div>
                        </form>
                        <ChangePasswordInput
                            passwordRequirements={this.state.passwordRequirements}
                            onSubmit={this.onChangePasswordInputSubmit}
                            onPasswordChanged={this.onPasswordChanged}
                            doValidation={this.state.validated}
                        />
                        {this.state.isNewPasswordInvalid &&
                            <div className="alert alert-warning mt-3">
                                {this.props.t("ChangePassword.PasswordIsInvalid")}
                            </div>
                        }
                        <button type="button" className="btn btn-primary" onClick={this.onSubmitPasswordChange}>{this.props.t("ChangePassword.ChangePasswordButton")}</button>
                        <div className="mt-3">
                            {this.state.areFieldInvalid &&
                                <div className="alert alert-warning">
                                    {this.props.t("ChangePassword.FieldsInvalid")}
                                </div>
                            }

                            {this.state.isChangePasswordFailure &&
                                <div className="alert alert-warning">
                                    {this.props.t("Global.UnexpectedError")}
                                </div>
                            }
                        </div>
                    </div>
                );


            case ProcessStateValues.error:
                return (
                    <div className="alert alert-warning">
                        {this.props.t("Global.UnexpectedError")}<br /><br />
                        <Link to="/registerRequest">{this.props.t("ChangePassword.RequestRegistrationLink")}</Link>
                    </div>
                );

            case ProcessStateValues.changePasswordComplete:
                return (
                    <div>
                        <div>{this.props.t("ChangePassword.ChangePasswordComplete")}</div>
                        <div>
                            <Link to="/dashboard">{this.props.t("Global.ContinueButton")}</Link>
                        </div>
                    </div>
                );

            default:
                return;
        }
    }

    // -----------------------------------------------------------
    // Events
    // -----------------------------------------------------------

    private onOldPasswordChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ oldPassword: event.target.value });
    }

    private inputKeypress(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();
            this.onSubmitPasswordChange();
        }
    }

    private onSubmitPasswordChange(): void {
        this.setState({ validated: true, processing: true }, async () => {
            if (this.isValid()) {
                // Submit registration request
                const result = await this.props.changePassword(this.state.oldPassword, this.state.password);
                if (result && result.success && result.payload) {
                    if (result.payload.success) {
                        // change password valid and complete
                        this.setState({ processState: ProcessStateValues.changePasswordComplete });
                    } else {
                        this.setState({
                            isOldPasswordInvalid: result.payload.oldPasswordInvalid,
                            isNewPasswordInvalid: result.payload.newPasswordInvalid,
                            passwordRequirements: result.payload.passwordRequirements,
                            isChangePasswordFailure: false,
                            areFieldInvalid: true,
                        });
                    }
                } else {
                    this.setState({ isChangePasswordFailure: true, isOldPasswordInvalid: false, isNewPasswordInvalid: false, areFieldInvalid: false });
                }
            } else {
                this.setState({ areFieldInvalid: true });
            }
            this.setState({ processing: false });
        });
    }

    private isValid(): boolean {
        let isValid = true;

        if (this.formElement) {
            if (!this.formElement.checkValidity()) {
                isValid = false;
            }
        }
        if (this.state.password !== this.state.confirmPassword) {
            isValid = false;
        }

        return isValid;
    }

    // -----------------------------------------------------------
    // ChangePasswordInput control callback functions
    // -----------------------------------------------------------

    // Called if user presses enter on the ChangePasswordInput control
    private onChangePasswordInputSubmit(password: string, confirmPassword: string) {
        this.setState({ password, confirmPassword }, () => {
            this.onSubmitPasswordChange();
        });
    }

    private async onPasswordChanged(password: string, confirmPassword: string) {
        this.setState({ password, confirmPassword }, () => {
            this.validatePasswordWhenUserStoppedTyping();
        });
    }

}

export default translateComponent(ChangePassword);



// WEBPACK FOOTER //
// ./src/components/ChangePassword/ChangePassword.tsx